import { buildProfilePayload } from '../../utils/profilePayload';
import { getProfileUserId } from '../../utils/identityUtils';
import { saveSharingConfig } from '../storage/userDataStorage';
import { supabase } from '../supabase';
import { logFunnelProfileShared } from '../analytics/eventLogger';
import { sendProfileDataResponse } from '../chat/profileService';
import { recordShare } from './connectionSharing';

/**
 * Commit the user's chosen shared fields for a connection.
 *
 * The single sharing-commit path, shared by the SharingSelectionOverlay (QR/chat
 * flow) and the inline SHARED list on the contact detail page. Builds the filtered
 * payload, persists config, updates the peer's received_contact, sends the profile
 * data response, logs analytics, and (first share only, PXP included) sends the
 * contact card.
 *
 * Callers own the selection UI and the post-commit flow (prompt chips, onComplete).
 *
 * @param {Object} params
 * @param {string} params.chatRoomId
 * @param {string[]} params.sharedFields - Selected field keys, including 'photoUrl'
 * @param {boolean} params.isFullName - Share full name vs first name only
 * @param {Object} params.profileData - The user's own profile data
 * @param {Object} params.connectionData - Connection data (anonData, connectionMetadata)
 * @param {Function} params.updateContactSharingStatus - Context method
 * @param {boolean} [params.updateGlobalDefaults=true] - Write the global next-connection
 *   defaults (profile_last_shared_fields, profile_name_sharing). One-tap quick shares
 *   pass false: a shortcut is not a preference declaration — without this, one Phone
 *   tap would shrink "Last share" to phone-only and force-flip the saved name default.
 *   Per-connection keys (sent_shared_fields_*, name_sharing_*) are always written.
 * @param {string} [params.shareMethod] - How the share was triggered, for analytics:
 *   'last_share' | 'phone' | 'edit' | 'overlay' | 'detail_page', and the new code
 *   page's sheet: 'code_full_profile' | 'code_custom' | 'code_group' | 'code_last_custom'
 * @param {Object} [params.shareKind] - the choice to record server side (new
 *   experience only; recordShare): { kind, on, off, groupId, exchange }. Absent =
 *   nothing recorded (the old design, and every flag-off share).
 * @returns {Promise<{contactShareMessage: Object|null}|null>} null if name is missing
 */
export const commitSharedFields = async ({
  chatRoomId,
  sharedFields,
  isFullName,
  profileData,
  connectionData,
  updateContactSharingStatus,
  updateGlobalDefaults = true,
  shareMethod,
  shareKind = null,
}) => {
  // SECURITY: Filtered payload — only selected fields leave the device.
  // profileUserId identifies the sharer so the recipient (esp. an anon) can resolve
  // peer activity — mirrors the qrDetection exchange payload. Without it, a custom
  // re-share overwrites profileData and drops the id.
  const updatedProfileData = buildProfilePayload(profileData, sharedFields, {
    name: isFullName ? profileData.name : profileData.name.split(' ')[0],
    nameSharing: isFullName ? 'full' : 'first',
    profileUserId: getProfileUserId(),
  });

  // Save sharing config (localStorage + Supabase dual-write)
  const nameShareValue = isFullName ? 'full' : 'first';
  saveSharingConfig(chatRoomId, {
    sentSharedFields: sharedFields,
    nameSharing: nameShareValue,
  });

  // Update peer's connection_data.received_contact so data recovery has the latest.
  // Uses SECURITY DEFINER RPC — direct upsert fails under RLS (cross-user write).
  const peerProfileUserId = connectionData?.anonData?.senderProfileUserId;
  if (peerProfileUserId) {
    supabase
      .rpc('update_peer_shared_data', {
        p_chat_room_id: chatRoomId,
        p_peer_profile_user_id: peerProfileUserId,
        p_received_contact: updatedProfileData,
      })
      .then(({ error }) => {
        if (error) console.warn('[sharing] Peer received_contact update failed:', error.message);
      });
  }

  // Save global defaults for next new connection (skipped for one-tap quick shares)
  if (updateGlobalDefaults) {
    localStorage.setItem('profile_name_sharing', nameShareValue);
    localStorage.setItem('profile_last_shared_fields', JSON.stringify(sharedFields));
  }

  // Analytics — exclude 'photoUrl' (always included, not a user selection)
  const contactTypesForAnalytics = sharedFields.filter((f) => f !== 'photoUrl');
  logFunnelProfileShared(
    chatRoomId,
    contactTypesForAnalytics,
    updatedProfileData.nameSharing,
    shareMethod
  );

  // Preserve existing metadata — meetingDate is set once at connection time.
  const savedMetadata = connectionData?.connectionMetadata;
  const metadataToSend = savedMetadata
    ? { ...savedMetadata, lastUpdated: new Date().toISOString() }
    : null;

  if (!updatedProfileData.name) {
    console.error('[sharing] Missing required field: name');
    return null;
  }

  // Send data (fire and forget)
  const senderProfileUserId = getProfileUserId();
  sendProfileDataResponse(chatRoomId, {
    profileData: updatedProfileData,
    connectionMetadata: metadataToSend,
    senderProfileUserId,
  });

  // The choice, recorded server side (049) — dormant unless NEW_EXPERIENCE
  // (recordShare checks); after the send, so delivery never waits on it; a
  // failure is logged there, never blocks the share.
  if (shareKind) await recordShare({ chatRoomId, ...shareKind });

  const contactFields = sharedFields.filter((field) => field !== 'photoUrl');
  if (contactFields.length > 0) {
    // Only mark as shared when actual contact fields are included (not just name)
    updateContactSharingStatus(chatRoomId, true);
  }

  // Display is the CALLER's job (social-track #13: chat shares speak, Detail
  // changes are silent) — chat surfaces post speech lines, the Detail-page
  // inline list and PXP commits stay silent by simply not posting. The card
  // message and its contact_card_sent_ once-only flag retired with
  // shares-as-speech.
  return { sharedContactTypes: contactFields };
};
